import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Search, Pencil, Trash2, Package, Printer } from "lucide-react";
import { toast } from "sonner";
import { LEVELS, ROWS, POS, locId, isLabelComp, breakdown } from "@/components/inventory/shelfShared";
import { printHtml, buildPullPlanHtml, buildShelvesHtml } from "@/components/inventory/shelfPrint";
import { AssignModal } from "@/components/inventory/AssignModal";
import { SlotDetailModal } from "@/components/inventory/SlotDetailModal";
import { PullListPanel } from "@/components/inventory/PullListPanel";
import { ReconcileBanner } from "@/components/inventory/ReconcileBanner";

export default function ShelfView() {
  const [components, setComponents] = useState([]);
  const [racks, setRacks] = useState([]);
  const [mos, setMos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [find, setFind] = useState("");
  const [category, setCategory] = useState("all");

  const [pullMoId, setPullMoId] = useState("");
  const [pullPlan, setPullPlan] = useState(null);
  const [pullLoading, setPullLoading] = useState(false);
  const [pulled, setPulled] = useState({});

  const [assignAt, setAssignAt] = useState(null);
  const [detailAt, setDetailAt] = useState(null);
  const [renaming, setRenaming] = useState(null);

  const load = async () => {
    try {
      const [c, r, m] = await Promise.all([api.get("/components"), api.get("/racks"), api.get("/manufacturing-orders")]);
      setComponents(c.data);
      setRacks(r.data);
      setMos(m.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const activeMos = useMemo(() => mos.filter((m) => m.placed && m.status !== "Completed"), [mos]);
  useEffect(() => {
    if (!pullMoId) { setPullPlan(null); return; }
    setPulled({});
    setPullLoading(true);
    api.get(`/manufacturing-orders/${pullMoId}/pull-plan`)
      .then((res) => setPullPlan(res.data))
      .catch((e) => { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); setPullPlan(null); })
      .finally(() => setPullLoading(false));
  }, [pullMoId]);

  // Locations to pull from for the selected order (drives rack highlighting).
  const pullByLocation = useMemo(() => {
    const m = {};
    (pullPlan?.components || []).forEach((c) => (c.locations || []).forEach((l) => {
      m[l.location] = { units: l.units, upc: l.units_per_case, upp: l.units_per_pallet, name: c.name };
    }));
    return m;
  }, [pullPlan]);

  const togglePulled = (loc) => setPulled((p) => ({ ...p, [loc]: !p[loc] }));

  const jumpTo = (loc) => {
    const el = document.getElementById(`slotcell-${loc}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("ring-4", "ring-amber-500");
    setTimeout(() => el.classList.remove("ring-4", "ring-amber-500"), 1500);
  };

  const printPlan = () => { if (pullPlan) printHtml(buildPullPlanHtml(pullPlan)); };

  const printShelves = () => {
    if (racks.length === 0) { toast.error("No racks to print yet."); return; }
    printHtml(buildShelvesHtml(racks, variants));
  };

  const variants = useMemo(() => {
    const out = [];
    components.forEach((c) => {
      if (/water/i.test(c.name || "")) return;  // Water: spring-sourced, not tracked as inventory
      (c.types || []).forEach((t) => {
      const upc = t.units_per_case || null;
      const cpp = t.cases_per_pallet || null;
      const upp = t.units_per_pallet || (upc && cpp ? upc * cpp : null);
      const designs = t.designs || [];
      if (isLabelComp(c.name) && designs.length) {
        designs.forEach((d) => out.push({
          key: `${t.id}::${d.id}`, component_id: c.id, category: c.name, type_id: t.id, design_id: d.id,
          name: c.name, sub: `${t.name} · ${d.name}`, label: `${c.name} · ${t.name} · ${d.name}`,
          upc, cpp, upp, stock: Number((t.design_stock || {})[d.id] || 0),
        }));
      } else {
        out.push({
          key: t.id, component_id: c.id, category: c.name, type_id: t.id, design_id: "",
          name: c.name, sub: t.name, label: `${c.name} · ${t.name}`,
          upc, cpp, upp, stock: Number(t.stock_qty || 0),
        });
      }
      });
    });
    return out;
  }, [components]);

  const categories = useMemo(() => Array.from(new Set(components.filter((c) => !/water/i.test(c.name || "")).map((c) => c.name))), [components]);

  const assignedByKey = useMemo(() => {
    const m = {};
    racks.forEach((r) => Object.values(r.slots || {}).forEach((s) => {
      const k = s.type_id + (s.design_id ? `::${s.design_id}` : "");
      m[k] = (m[k] || 0) + Number(s.units || 0);
    }));
    return m;
  }, [racks]);

  const matchesFind = (slot) => {
    const q = find.trim().toLowerCase();
    if (!q || !slot) return false;
    return [slot.component_name, slot.type_name, slot.design_name].filter(Boolean).join(" ").toLowerCase().includes(q);
  };

  const addRack = async () => {
    try {
      const { data } = await api.post("/racks", {});
      setRacks((p) => [...p, data]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const deleteRack = async (rack) => {
    if (!window.confirm(`Delete ${rack.name}? Any inventory assigned here returns to Unassigned.`)) return;
    try {
      await api.delete(`/racks/${rack.id}`);
      setRacks((p) => p.filter((r) => r.id !== rack.id));
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const saveRename = async () => {
    if (!renaming?.name.trim()) return;
    try {
      const { data } = await api.put(`/racks/${renaming.id}`, { name: renaming.name.trim() });
      setRacks((p) => p.map((r) => (r.id === data.id ? data : r)));
      setRenaming(null);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const clearSlot = async (rackId, location) => {
    try {
      const { data } = await api.delete(`/racks/${rackId}/slots/${location}`);
      setRacks((p) => p.map((r) => (r.id === data.id ? data : r)));
      setDetailAt(null);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const reconcileNow = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/racks/reconcile");
      const parts = [];
      if (data.removed_orphan_slots > 0) parts.push(`${data.removed_orphan_slots} orphaned slot${data.removed_orphan_slots === 1 ? "" : "s"} removed`);
      (data.trimmed || []).forEach((t) => parts.push(`${t.name}: trimmed ${t.units} units`));
      toast.success(parts.length ? `Reconciled — ${parts.join("; ")}` : "Nothing to reconcile");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    setReconciling(false);
  };

  if (loading) {
    return <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  const unassignedList = variants
    .map((v) => ({ ...v, assigned: assignedByKey[v.key] || 0 }))
    .map((v) => ({ ...v, unassigned: v.stock - v.assigned }))
    .filter((v) => v.unassigned > 0.0001)
    .filter((v) => category === "all" || v.category === category);

  const reconciliation = variants
    .map((v) => ({ ...v, assigned: assignedByKey[v.key] || 0 }))
    .filter((v) => v.assigned > 0.0001 && v.assigned - Math.max(0, v.stock) > 0.0001);

  const knownTypeIds = new Set(components.flatMap((c) => (c.types || []).map((t) => t.id)));
  const orphanSlots = racks.flatMap((r) => Object.entries(r.slots || {})
    .filter(([, s]) => !knownTypeIds.has(s.type_id))
    .map(([loc, s]) => ({ loc, name: [s.component_name, s.type_name, s.design_name].filter(Boolean).join(" · ") || "Unknown item", units: Number(s.units || 0) })));
  const needsReconcile = reconciliation.length > 0 || orphanSlots.length > 0;

  const renderSlot = (rack, loc) => {
    const slot = (rack.slots || {})[loc];
    const hit = matchesFind(slot);
    const pull = pullByLocation[loc];
    const isPulled = pull && pulled[loc];
    return (
      <button key={loc} id={`slotcell-${loc}`}
        onClick={() => slot ? setDetailAt({ rackId: rack.id, location: loc }) : setAssignAt({ rackId: rack.id, location: loc })}
        className={`block w-full min-w-0 overflow-hidden min-h-[58px] rounded-sm border p-1.5 text-left transition-colors ${slot ? "border-[#002FA7]/40 bg-[#002FA7]/5 hover:bg-[#002FA7]/10" : "border-dashed border-slate-300 bg-white hover:border-slate-400"} ${pull ? (isPulled ? "!border-emerald-400 bg-emerald-50 ring-2 ring-emerald-300" : "!border-amber-500 bg-amber-50 ring-2 ring-amber-400 hover:bg-amber-100") : hit ? "ring-2 ring-amber-400" : ""}`}
        data-testid={`slot-${loc}`}>
        <div className="truncate text-[8px] font-semibold uppercase tracking-wide text-slate-400">{loc}</div>
        {slot ? (
          <div className="mt-0.5 flex items-start gap-1.5">
            <div className="min-w-0 flex-1">
              <div className="break-words text-[11px] font-bold leading-tight text-slate-900">{slot.component_name}</div>
              <div className="break-words text-[9px] leading-tight text-slate-500">{slot.type_name}{slot.design_name ? ` · ${slot.design_name}` : ""}</div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-0.5 text-right">
              {breakdown(slot.units, slot.units_per_case, slot.units_per_pallet).map((p, i) => (
                <span key={`${p}-${i}`} className="whitespace-nowrap text-[9px] leading-tight text-slate-600">{p}</span>
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-1.5 text-center text-[10px] text-slate-300">Empty</div>
        )}
        {pull && (
          <div className={`mt-1 rounded-sm px-1 py-0.5 text-center text-[8px] font-bold uppercase leading-tight tracking-wide text-white ${isPulled ? "bg-emerald-500" : "bg-amber-500"}`} data-testid={`pull-badge-${loc}`}>
            {isPulled ? "Pulled \u2713" : `Pull ${breakdown(pull.units, pull.upc, pull.upp).join(" · ")}`}
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-6" data-testid="shelf-view">
      <PullListPanel
        activeMos={activeMos} pullMoId={pullMoId} onSelectMo={setPullMoId}
        pullPlan={pullPlan} pullLoading={pullLoading} pulled={pulled}
        onTogglePulled={togglePulled} onJump={jumpTo} onPrint={printPlan}
      />

      <div className="border border-slate-200">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-100 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">Unassigned Inventory</span>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-8 w-44 rounded-sm bg-white text-xs" data-testid="shelf-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto p-3" data-testid="unassigned-list">
          {unassignedList.length === 0 ? (
            <p className="w-full py-4 text-center text-xs text-slate-400">Everything in stock is assigned to a shelf.</p>
          ) : unassignedList.map((v) => (
            <div key={v.key} className="w-[200px] shrink-0 rounded-sm border border-slate-200 p-2.5" data-testid={`unassigned-${v.key}`}>
              <p className="truncate text-sm font-semibold text-slate-900">{v.name}</p>
              <p className="truncate text-[11px] text-slate-500">{v.sub}</p>
              <p className="mt-1 text-xs text-slate-600">{breakdown(v.unassigned, v.upc, v.upp).join(" · ")}</p>
              <Button size="sm" variant="outline" className="mt-2 h-7 w-full rounded-sm text-xs"
                onClick={() => setAssignAt({ preselect: v.key })} data-testid={`assign-from-unassigned-${v.key}`}>
                Assign to a slot
              </Button>
            </div>
          ))}
        </div>
      </div>

      {needsReconcile && (
        <ReconcileBanner reconciliation={reconciliation} orphanSlots={orphanSlots} reconciling={reconciling} onReconcile={reconcileNow} />
      )}

      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative max-w-sm flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find a component in the racks…" className="rounded-sm pl-9" data-testid="shelf-find-input" />
          </div>
          <Button onClick={printShelves} variant="outline" size="sm" className="shrink-0 rounded-sm" data-testid="print-shelves-button"><Printer className="mr-1 h-4 w-4" /> Print Shelves</Button>
          <Button onClick={addRack} variant="outline" size="sm" className="shrink-0 rounded-sm" data-testid="add-rack-button"><Plus className="mr-1 h-4 w-4" /> Add Rack</Button>
        </div>

        {racks.length === 0 ? (
          <div className="flex flex-col items-center justify-center border border-dashed border-slate-300 py-20 text-center">
            <Package className="h-9 w-9 text-slate-300" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-slate-500">No racks yet. Click <b>Add Rack</b> to build your first 32-position rack.</p>
          </div>
        ) : (
          <div className="flex items-start gap-4 overflow-x-auto pb-3" data-testid="rack-strip">
            {racks.map((rack) => (
              <div key={rack.id} className="w-[340px] shrink-0" data-testid={`rack-${rack.code}`}>
                <div className="mb-2 flex items-center justify-between gap-1 rounded-sm bg-slate-100 px-2 py-1.5">
                  <span className="truncate text-sm font-bold text-slate-800" data-testid={`rack-name-${rack.code}`}>{rack.name}</span>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button onClick={() => setRenaming({ id: rack.id, name: rack.name })} className="p-1 text-slate-400 hover:text-slate-700" data-testid={`rack-rename-${rack.code}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => deleteRack(rack)} className="p-1 text-slate-400 hover:text-red-600" data-testid={`rack-delete-${rack.code}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <div className="space-y-3">
                  {LEVELS.map((level) => (
                    <div key={level} className="border border-slate-200">
                      <div className="border-b border-slate-200 bg-slate-800 px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-white">Level {level}</div>
                      <div className="flex gap-1.5 p-1.5">
                        {ROWS.map((row) => (
                          <div key={row} className="min-w-0 flex-1">
                            <div className="mb-1 text-center text-[10px] font-semibold text-slate-500">Row {row}</div>
                            <div className="flex flex-col gap-1.5">
                              {[...POS].reverse().map((p) => renderSlot(rack, locId(rack.code, level, row, p)))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {assignAt && (
        <AssignModal
          racks={racks} variants={variants} assignedByKey={assignedByKey} category={category}
          initialRackId={assignAt.rackId} initialLocation={assignAt.location} preselectKey={assignAt.preselect}
          existingSlot={assignAt.rackId && assignAt.editing ? (racks.find((r) => r.id === assignAt.rackId)?.slots?.[assignAt.location]) : null}
          onClose={() => setAssignAt(null)}
          onSaved={(rack) => { setRacks((p) => p.map((r) => (r.id === rack.id ? rack : r))); setAssignAt(null); }}
        />
      )}

      {detailAt && (() => {
        const rack = racks.find((r) => r.id === detailAt.rackId);
        const slot = rack?.slots?.[detailAt.location];
        if (!slot) return null;
        return (
          <SlotDetailModal
            rack={rack} racks={racks} location={detailAt.location} slot={slot}
            onClose={() => setDetailAt(null)}
            onEdit={() => { setDetailAt(null); setAssignAt({ rackId: rack.id, location: detailAt.location, editing: true }); }}
            onClear={() => clearSlot(rack.id, detailAt.location)}
            onMoved={() => { setDetailAt(null); load(); }}
          />
        );
      })()}

      {renaming && (
        <Dialog open onOpenChange={(o) => { if (!o) setRenaming(null); }}>
          <DialogContent className="rounded-sm sm:max-w-sm" data-testid="rename-rack-dialog">
            <DialogHeader><DialogTitle>Rename rack</DialogTitle></DialogHeader>
            <Input value={renaming.name} onChange={(e) => setRenaming((s) => ({ ...s, name: e.target.value }))} className="rounded-sm" data-testid="rename-rack-input" autoFocus />
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={() => setRenaming(null)}>Cancel</Button>
              <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={saveRename} data-testid="rename-rack-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
